/**
 * Shared currency formatting utility for Botswana Pula (BWP).
 * Produces format: "P 12,000.00"
 *   - P prefix with a space
 *   - Thousand separators (commas)
 *   - Exactly 2 decimal places
 *   - Negatives in accounting parentheses: "(P 563,820.00)"
 *
 * Every user-facing currency amount — dashboards, ledgers, statements,
 * invoices, modals, drill-downs — must go through this. Do NOT hand-roll
 * `` `P${x.toFixed(2)}` `` or a local `Intl.NumberFormat` in a component;
 * a CI check (scripts/check-currency-formatting.js) flags new ones.
 */

const pulaFormatter = new Intl.NumberFormat('en-BW', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Formats a numeric value as Botswana Pula.
 * @param {number|string} value - The monetary value to format.
 * @param {object} [opts]
 * @param {boolean} [opts.parensForNegative=true] - Render negatives as "(P 1.00)".
 *        Pass false only where a leading minus is genuinely wanted (rare).
 * @returns {string} e.g. "P 12,000.00", "(P 563,820.00)"
 */
export const formatCurrency = (value, opts = {}) => {
  const { parensForNegative = true } = opts;
  const num = Number(value);
  const safe = isNaN(num) ? 0 : num;
  if (safe < 0 && parensForNegative) {
    return `(P ${pulaFormatter.format(Math.abs(safe))})`;
  }
  return `P ${pulaFormatter.format(safe)}`;
};

/**
 * Formats a numeric value as Pula without the P prefix.
 * Useful for table columns that already show "Amount (P)" in the header.
 * Negatives still render in parentheses unless opted out.
 * @param {number|string} value - The monetary value.
 * @param {object} [opts]
 * @param {boolean} [opts.parensForNegative=true]
 * @returns {string} e.g. "12,000.00", "(563,820.00)"
 */
export const formatNumber = (value, opts = {}) => {
  const { parensForNegative = true } = opts;
  const num = Number(value);
  const safe = isNaN(num) ? 0 : num;
  if (safe < 0 && parensForNegative) {
    return `(${pulaFormatter.format(Math.abs(safe))})`;
  }
  return pulaFormatter.format(safe);
};

export default formatCurrency;
